"use client";

import { AnimatePresence, motion } from "framer-motion";
import Image from "../ui/OptimizedImage";
import Link from "next/link";

type MobileNavProps = {
    isMobileMenuOpen: boolean;
    onClose: () => void;
    isSignedIn: boolean | undefined;
    isHost: boolean | null;
    displayName: string;
    userImageUrl: string | null | undefined;
    userEmail: string | null | undefined;
    authHref: string;
    onNavigate: (href: string) => void;
    onSignOut: () => void;
};

const guestMenuItems = [
    { label: "My tickets", href: "/my-tickets" },
    { label: "My profile", href: "/profile" },
];

const hostMenuItems = [
    { label: "My events", href: "/dashboard?tab=events" },
    { label: "My tickets", href: "/my-tickets" },
    { label: "Analytics", href: "/dashboard?tab=analytics" },
    { label: "Settings", href: "/dashboard?tab=settings" },
];

export function MobileNav({
    isMobileMenuOpen,
    onClose,
    isSignedIn,
    isHost,
    displayName,
    userImageUrl,
    userEmail,
    authHref,
    onNavigate,
    onSignOut,
}: MobileNavProps) {
    return (
        <AnimatePresence>
            {isMobileMenuOpen && (
                <motion.div
                    className="fixed inset-0 z-[70] flex flex-col overflow-y-auto bg-black px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] text-[#EDEDED]"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className="mb-6 flex items-center justify-between relative">
                        <Link
                            href="/"
                            className="flex items-center justify-center absolute left-1/2 -translate-x-1/2"
                            onClick={onClose}
                        >
                            <Image
                                src="/images/logo.svg"
                                alt="Hopp"
                                width={100}
                                height={28}
                                className="h-7 w-auto"
                            />
                        </Link>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--hopp-panel-strong)] text-[var(--hopp-muted)] transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)]"
                            aria-label="Close menu"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <Link
                        href="/events"
                        className="mb-3 flex min-h-[48px] w-full items-center justify-between rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-5 py-3 text-sm font-semibold text-[#EDEDED]"
                        onClick={onClose}
                    >
                        Browse Events
                        <span className="text-[var(--hopp-muted)]">→</span>
                    </Link>
                    <Link
                        href="/map"
                        className="mb-6 flex min-h-[48px] w-full items-center justify-between rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-5 py-3 text-sm font-semibold text-[#EDEDED]"
                        onClick={onClose}
                    >
                        Map
                        <span className="text-[var(--hopp-muted)]">→</span>
                    </Link>
                    {isSignedIn ? (
                        <div className="flex min-h-0 flex-1 flex-col space-y-6">
                            <div className="rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)]/70 p-4">
                                <div className="flex items-center gap-4">
                                    <div className="relative h-12 w-12 overflow-hidden rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-ink)]/60">
                                        {userImageUrl ? (
                                            <Image
                                                src={userImageUrl}
                                                alt={displayName}
                                                fill
                                                className="object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-[var(--hopp-text)]">
                                                {displayName.slice(0, 2).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-[var(--hopp-text)]">
                                            {displayName}
                                        </p>
                                        <p className="truncate text-[10px] uppercase tracking-[0.3em] text-[var(--hopp-muted)]">
                                            {userEmail ?? "Guest account"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3 border-b border-[var(--hopp-panel-strong)] pb-6 pr-1 text-sm font-semibold">
                                <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--hopp-muted)]">
                                    Guest
                                </p>
                                {guestMenuItems.map((item) => (
                                    <button
                                        key={item.label}
                                        type="button"
                                        onClick={() => {
                                            onNavigate(item.href);
                                            onClose();
                                        }}
                                        className="flex min-h-[44px] w-full items-center justify-between"
                                    >
                                        {item.label}
                                        <span className="text-[var(--hopp-muted)]">→</span>
                                    </button>
                                ))}
                            </div>

                            {isHost === true ? (
                                <div className="space-y-3 pr-1 text-sm font-semibold">
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--hopp-muted)]">
                                        Host
                                    </p>
                                    {hostMenuItems.map((item) => (
                                        <button
                                            key={`host-${item.label}`}
                                            type="button"
                                            onClick={() => {
                                                onNavigate(item.href);
                                                onClose();
                                            }}
                                            className="flex min-h-[44px] w-full items-center justify-between"
                                        >
                                            {item.label}
                                            <span className="text-[var(--hopp-muted)]">→</span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="pr-1 text-sm font-semibold">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onNavigate("/onboarding?start=hostType");
                                            onClose();
                                        }}
                                        className="flex min-h-[44px] w-full items-center justify-between text-[var(--hopp-sapphire)]"
                                    >
                                        Become a host
                                        <span className="text-[var(--hopp-muted)]">→</span>
                                    </button>
                                </div>
                            )}

                            <div className="pt-2">
                                <button
                                    onClick={() => {
                                        onSignOut();
                                        onClose();
                                    }}
                                    className="w-full rounded-xl border border-red-500/30 bg-[rgba(255,120,120,0.08)] py-3 text-center text-xs font-semibold uppercase tracking-[0.3em] text-red-200"
                                >
                                    Log out
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-auto pb-10">
                            <div className="flex flex-col gap-4">
                                <Link
                                    href={authHref}
                                    className="w-full rounded-xl bg-[var(--hopp-sapphire)] py-3 text-center text-sm font-semibold text-white"
                                    onClick={onClose}
                                >
                                    Sign in
                                </Link>
                                <Link
                                    href={authHref}
                                    className="w-full py-2 text-center text-sm text-[var(--hopp-muted)]"
                                    onClick={onClose}
                                >
                                    Create account
                                </Link>
                            </div>
                        </div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
